import { RelativeTime } from '@/components/RelativeTime';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { useDeviceAddresses } from '@/hooks/useDeviceAddresses';
import { type AddressRow, groupAddresses } from './deviceAddressRows';

const TH = 'text-left py-1.5 px-3 text-xs font-medium text-muted-foreground uppercase tracking-wider';

function AddressTable({ title, rows, showVrf }: { title: string; rows: AddressRow[]; showVrf: boolean }) {
  if (rows.length === 0) return null;
  return (
    <div>
      <h3 className="text-xs font-medium uppercase tracking-wide text-muted-foreground mb-1.5">{title}</h3>
      <div className="overflow-x-auto rounded-md border border-border">
        <table className="w-full text-sm">
          <thead className="border-b border-border-subtle">
            <tr>
              <th className={TH}>Address</th>
              <th className={TH}>Interface</th>
              {showVrf ? <th className={TH}>VRF</th> : null}
              <th className={TH}>Seen by</th>
              <th className={TH}>Last seen</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border-subtle">
            {rows.map((r) => (
              <tr key={r.ipAddress}>
                <td className="py-1.5 px-3">
                  <span className="inline-flex items-center gap-1.5 flex-wrap">
                    <span className="font-mono break-all">{r.ipAddress}</span>
                    {r.isPrimary ? (
                      <Badge
                        variant="secondary"
                        className="font-normal"
                        title="The address this device is identified and polled by"
                      >
                        Primary
                      </Badge>
                    ) : null}
                    {r.internal ? (
                      <span
                        className="text-[11px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-muted text-muted-foreground"
                        title="Reported by the device, but not reachable from the network"
                      >
                        {r.internal}
                      </span>
                    ) : null}
                  </span>
                </td>
                <td className="py-1.5 px-3 font-mono text-xs">{r.interfaces.join(', ') || '—'}</td>
                {showVrf ? <td className="py-1.5 px-3 font-mono text-xs">{r.vrfs.join(', ') || '—'}</td> : null}
                <td className="py-1.5 px-3 text-xs">{r.sources.join(', ') || '—'}</td>
                <td className="py-1.5 px-3 text-xs text-muted-foreground whitespace-nowrap">
                  <RelativeTime value={r.lastSeenAt} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/** Every address a monitored device is known by, on its Overview. */
export default function DeviceAddressesSection({ deviceId }: { deviceId: string | undefined }) {
  const { data, isLoading, isError } = useDeviceAddresses(deviceId);

  let body;
  if (isLoading) {
    body = <Skeleton className="h-24 w-full" />;
  } else if (isError) {
    body = <p className="text-sm text-muted-foreground">Couldn't load this device's addresses.</p>;
  } else if (!data || data.length === 0) {
    body = <p className="text-sm text-muted-foreground">No addresses recorded yet.</p>;
  } else {
    const { ipv4, ipv6 } = groupAddresses(data);
    // Only devices that name VRFs get the column.
    const showVrf = [...ipv4, ...ipv6].some((r) => r.vrfs.length > 0);
    body = (
      <div className="space-y-4">
        <AddressTable title="IPv4" rows={ipv4} showVrf={showVrf} />
        <AddressTable title="IPv6" rows={ipv6} showVrf={showVrf} />
      </div>
    );
  }

  return (
    <section className="bg-card border border-border rounded-lg p-5 mt-4">
      <h2 className="text-sm font-semibold mb-3">IP addresses</h2>
      {body}
    </section>
  );
}
